import { ReactElement, useState } from "react";
import cx from "clsx";
import { Badge, Box, Text, Truncate } from "@twilio-paste/core";
import { NewIcon } from "@twilio-paste/icons/esm/NewIcon";
import { LinkExternalIcon } from "@twilio-paste/icons/esm/LinkExternalIcon";
import { ThumbsDownIcon } from "@twilio-paste/icons/esm/ThumbsDownIcon";
import { ThumbsUpIcon } from "@twilio-paste/icons/esm/ThumbsUpIcon";

import { copy } from "../definitions";

import TrackingAnchor from "../../trackingAnchor";
import RouterAnchor from "../../routerAnchor";
import TrackingButton from "../../trackingButton";
import styles from "../SearchResults.module.css";
import {
  RelevanceValue,
  ResultProps,
} from "@twiliointernal/e2-commons-shared-definitions";

const Result = ({
  body,
  id,
  userMethods,
  isExternal,
  isRecommended,
  title,
  url,
  feedbackPrompt = copy.feedback.prompt,
  trackingMethods,
}: ResultProps): ReactElement => {
  const [relevance, updateRelevance] = useState<RelevanceValue | undefined>(
    undefined
  );

  const iconSize = "sizeIcon20";

  const feedbackHasBeenProvided = relevance !== undefined;
  const isRelevant = relevance === RelevanceValue.relevant;
  const positiveSelected = feedbackHasBeenProvided && isRelevant;
  const negativeSelected = feedbackHasBeenProvided && !isRelevant;

  const handleOnResultClick = (): void => {
    userMethods.onResultClick();
  };

  const handleOnFeedbackClick = (relevanceFeedback: RelevanceValue): void => {
    updateRelevance(relevanceFeedback);

    userMethods.onFeedbackClick(relevanceFeedback);
  };

  const renderResultAnchor = (): ReactElement => {
    const baseProps = {
      trackEvent: trackingMethods?.resultClick,
      onClick: handleOnResultClick,
      color: "colorText",
    };

    const anchorChildren = (
      <Box className={styles.resultTitleContainer}>
        <Truncate title={title}>{title}</Truncate>

        {isExternal && (
          <Box minWidth={iconSize}>
            <LinkExternalIcon
              title={`${title} link icon`}
              decorative={false}
              size={iconSize}
            />
          </Box>
        )}
      </Box>
    );

    const anchorComponent = isExternal ? (
      <TrackingAnchor href={url} {...baseProps}>
        {anchorChildren}
      </TrackingAnchor>
    ) : (
      <RouterAnchor to={url} {...baseProps}>
        {anchorChildren}
      </RouterAnchor>
    );

    const badgeComponent = (
      <Badge as="span" variant="new">
        <NewIcon decorative />

        {copy.result.recommendedBadge}
      </Badge>
    );

    return (
      <Box className={styles.resultHeader}>
        <Text as="span" fontSize={"fontSize60"} marginBottom={"space80"}>
          {anchorComponent}
        </Text>

        {isRecommended && badgeComponent}
      </Box>
    );
  };

  return (
    <Box id={id} className={styles.resultContainer}>
      <Box as="div" className={styles.resultContentContainer}>
        <Box
          as="article"
          color="colorTextBrandInverse"
          className={styles.resultCard}
        >
          {renderResultAnchor()}

          <Box>
            <Text
              as="p"
              dangerouslySetInnerHTML={{ __html: body }}
              fontSize={"fontSize30"}
              lineHeight={"lineHeight40"}
            />
          </Box>

          <Box className={styles.resultFeedbackContainer}>
            <Box as="div" className={styles.resultFeedbackIconButtonsContainer}>
              <Box
                as="div"
                className={cx({
                  [styles.resultFeedbackButtonContainer]: true,
                  [styles.resultFeedbackButtonContainerNotSelected]:
                    negativeSelected,
                  [styles.resultFeedbackButtonContainerPositiveSelected]:
                    positiveSelected,
                })}
              >
                <TrackingButton
                  disabled={feedbackHasBeenProvided}
                  variant="link"
                  onClick={(): void =>
                    handleOnFeedbackClick(RelevanceValue.relevant)
                  }
                  trackEvent={(): void =>
                    trackingMethods?.feedbackClick(RelevanceValue.relevant)
                  }
                >
                  <Box
                    className={cx({
                      [styles.resultFeedbackIconContainer]: true,
                      [styles.resultPositiveFeedbackIconContainer]: true,
                      [styles.resultFeedbackIconContainerSelected]:
                        positiveSelected,
                    })}
                  >
                    <ThumbsUpIcon
                      decorative={false}
                      title={copy.feedback.positiveTitle}
                      size={iconSize}
                    />
                  </Box>
                </TrackingButton>
              </Box>

              <Box
                as="div"
                className={cx({
                  [styles.resultFeedbackButtonContainer]: true,
                  [styles.resultFeedbackButtonContainerNotSelected]:
                    positiveSelected,
                  [styles.resultFeedbackButtonContainerNegativeSelected]:
                    negativeSelected,
                })}
              >
                <TrackingButton
                  disabled={feedbackHasBeenProvided}
                  variant="link"
                  onClick={(): void =>
                    handleOnFeedbackClick(RelevanceValue.notRelevant)
                  }
                  trackEvent={(): void =>
                    trackingMethods?.feedbackClick(RelevanceValue.notRelevant)
                  }
                >
                  <Box
                    className={cx({
                      [styles.resultFeedbackIconContainer]: true,
                      [styles.resultNegativeFeedbackIconContainer]: true,
                      [styles.resultFeedbackIconContainerSelected]:
                        negativeSelected,
                    })}
                  >
                    <ThumbsDownIcon
                      decorative={false}
                      title={copy.feedback.negativeTitle}
                      size={iconSize}
                    />
                  </Box>
                </TrackingButton>
              </Box>
            </Box>

            <Box
              as="div"
              className={cx({
                [styles.resultPromptContainer]: true,
                [styles.resultPromptContainerSelected]: feedbackHasBeenProvided,
              })}
            >
              <Text
                as="p"
                fontSize={"fontSize20"}
                fontWeight={
                  feedbackHasBeenProvided
                    ? "fontWeightMedium"
                    : "fontWeightNormal"
                }
              >
                {feedbackHasBeenProvided
                  ? copy.feedback.thankYou
                  : feedbackPrompt}
              </Text>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Result;
